'use client'
// POSSESSION-MODEL (2026-07-18): the desk's own author-state indicator — the
// menu's far-right slot (where AuthEntry used to sit; identity moved to the
// page top-right). The MAIN msg rules the menu, so the menu shows the main
// msg's state:
//   'open'  — no edit pin ('' / '0'): everyone possesses; full tools.
//   'pin'   — edit-unlocked via a matched pin / owner editCode.
//   locked  — edit-locked: answers-only surface (QR + new answer).
//
// EDIT-PIN ENTRY: the locked badge is the way IN to possession — clicking it
// reveals a small pin form that POSTs to the shared session-unlock route
// (codekey + show_pin, the LockedScreen contract). check_msg matches the
// offered pin against the EDIT pin too, so entering the correct edit
// password re-boots the desk possessed. Pins ride the POST body, never URLs.
//
// SKELETON component: structure + md-* contract classes only; visuals arrive
// through the DeskStateBadgeSkin the owning surface passes (no-CSS rule).

import { useState } from 'react'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { selectAccessGrant, selectDeskPossession, selectMainState } from '@/msgdeskRuntime/state'
import { UNLOCK_ACTION } from '../Blocking/LockedScreen'

/** POSSESSION-EXIT: forget the entered pin for this desk — the session pin
 * is dropped and the reloaded boot re-engages BOTH the show and the edit
 * lock. Hidden for the owner (their editCode re-offers on every boot; their
 * exit is Sign out, top-right). */
function ExitPinForm({ codeKey, skin, title }: { codeKey: string; skin: DeskStateBadgeSkin; title: string }) {
  return (
    <form className={`md-desk-state__exit ${skin.form ?? ''}`} method="post" action={UNLOCK_ACTION}>
      <input type="hidden" name="codekey" value={codeKey} />
      <input type="hidden" name="forget" value="1" />
      <button type="submit" className={`md-desk-state__button ${skin.button ?? ''}`} title={title}>
        exit
      </button>
    </form>
  )
}

export interface DeskStateBadgeSkin {
  root?: string
  possessedOpen?: string
  possessedPin?: string
  locked?: string
  /** the clickable "Answers only" label that reveals the pin form */
  label?: string
  form?: string
  input?: string
  button?: string
}

export function DeskStateBadge({ skin = {} }: { skin?: DeskStateBadgeSkin }) {
  const { state } = useSubsStore()
  const possession = selectDeskPossession(state)
  const isOwner = selectAccessGrant(state).viewer.is_owner
  const codeKey = selectMainState(state).codeKey || ''
  const [formOpen, setFormOpen] = useState(false)

  if (possession.possessed) {
    const open = possession.via === 'open'
    return (
      <span
        className={`md-desk-state md-desk-state--${open ? 'open' : 'possessed'} ${skin.root ?? ''} ${(open ? skin.possessedOpen : skin.possessedPin) ?? ''}`}
        title={open
          ? 'This message has no edit PIN. Anyone can edit it, but it cannot be used to sign in.'
          : 'You act as this message'}
      >
        {open ? (
          <>
            Editable · <span className="md-desk-state__no-pin" style={{ color: '#dc2626' }}>no pin</span>
          </>
        ) : 'Owner'}
        {!open && !isOwner ? (
          <ExitPinForm codeKey={codeKey} skin={skin} title="Stop acting as this message — relock the page" />
        ) : null}
      </span>
    )
  }

  return (
    <span className={`md-desk-state md-desk-state--locked ${skin.root ?? ''} ${skin.locked ?? ''}`}>
      <button
        type="button"
        className={`md-desk-state__label ${skin.label ?? ''}`}
        title="Edit-locked — enter the editpin to act as this message owner"
        onClick={() => setFormOpen(open => !open)}
      >
        Visitor
      </button>
      {possession.showVia === 'pin' && !isOwner && !formOpen ? (
        // Viewing was pin-unlocked while edit stays locked: exiting drops the
        // stored pin, so the show lock returns too.
        <ExitPinForm codeKey={codeKey} skin={skin} title="Forget the entered password — relock viewing" />
      ) : null}
      {formOpen ? (
        <form
          className={`md-desk-state__form ${skin.form ?? ''}`}
          method="post"
          action={UNLOCK_ACTION}
        >
          <input type="hidden" name="codekey" value={codeKey} />
          <input
            type="password"
            name="show_pin"
            placeholder="Edit password"
            autoComplete="off"
            autoFocus
            className={`md-desk-state__input ${skin.input ?? ''}`}
          />
          <button type="submit" className={`md-desk-state__button ${skin.button ?? ''}`}>
            Unlock
          </button>
        </form>
      ) : null}
    </span>
  )
}
